import { useUsersWithRoles, UserWithRoleDTO } from '../../user-management';
import { useTenantDomainsLookup } from './useTenantDomainsLookup';
import { useTenantAllSubjects } from './useTenantAllSubjects';
import { UserQueryParams, UsersWithRolesResult } from '../../user-management/types';

export interface TenantUserTableData {
  id: string;
  name: string;
  email: string;
  roles: string[];
  status: 'invited' | 'active' | 'inactive';
  createdAt: string;
  updatedAt?: string;
  // Resolved institute and course names for display
  instituteNames?: string[];
  courseNames?: string[];
}

// Helper function to detect if search term looks like an email
const looksLikeEmail = (term: string): boolean => {
  return term.includes('@') && term.includes('.');
};

// Hook for fetching users in a tenant with pagination and search support
export const useTenantUsers = (
  tenantId: string,
  paginationParams?: { page?: number; limit?: number },
  searchParams?: { searchQuery?: string }
) => {
  // Build search parameters intelligently
  const searchFields = searchParams?.searchQuery
    ? (() => {
        const term = searchParams.searchQuery.trim();
        if (!term) return {};

        // If it looks like an email, search primarily by email but also include name
        if (looksLikeEmail(term)) {
          return { email: term, name: term };
        }

        // For general terms, search both name and email
        return { name: term, email: term };
      })()
    : {};

  const queryParams: UserQueryParams = {
    tenantId,
    includeUsersWithoutRoles: true, // Include users even if they have no roles
    page: paginationParams?.page || 1,
    limit: paginationParams?.limit || 10,
    ...searchFields,
  };

  const result: UsersWithRolesResult = useUsersWithRoles(queryParams, {
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    keepPreviousData: true, // Keep previous data while fetching new page
  });

  return result;
};

// Hook for transforming tenant users data for table display
export const useTenantUsersTableData = (
  tenantId: string,
  paginationParams?: { page?: number; limit?: number },
  searchParams?: { searchQuery?: string }
) => {
  const {
    data: tenantUsers,
    totalCount,
    pagination,
    ...queryResult
  } = useTenantUsers(tenantId, paginationParams, searchParams);
  const { domainLookup, isLoading: domainsLoading } = useTenantDomainsLookup(tenantId);
  const { subjectLookup, isLoading: subjectsLoading } = useTenantAllSubjects(tenantId);

  const tableData: TenantUserTableData[] = (tenantUsers || []).map((user: UserWithRoleDTO) => {
    // Extract unique domain and subject names from user roles
    const uniqueDomainNames = new Set<string>();
    const uniqueCourseNames = new Set<string>();

    if (user.roles) {
      user.roles.forEach((roleInfo) => {
        // Add domain name if domainId exists
        if (roleInfo.domainId) {
          const domainInfo = domainLookup.get(roleInfo.domainId);
          if (domainInfo) {
            const displayName = domainInfo.code ? `${domainInfo.code}` : domainInfo.name;
            uniqueDomainNames.add(displayName);
          }
        }

        // Add subject name if subjectId exists
        if (roleInfo.subjectId) {
          const subjectInfo = subjectLookup.get(roleInfo.subjectId);
          if (subjectInfo) {
            uniqueCourseNames.add(subjectInfo.code);
          }
        }
      });
    }

    return {
      id: user.id,
      name: user.name || user.email, // Fallback to email if name not available
      email: user.email,
      roles: user.roles ? user.roles.map((role) => role.role) : [],
      status: user.status,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      instituteNames: Array.from(uniqueDomainNames),
      courseNames: Array.from(uniqueCourseNames),
    };
  });

  const { isLoading: usersLoading, ...otherQueryResult } = queryResult;

  return {
    data: tableData,
    totalCount,
    pagination,
    isLoading: usersLoading || domainsLoading || subjectsLoading,
    ...otherQueryResult,
  };
};
